import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import PageHero from "@/components/PageHero";
import api from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Award } from "lucide-react";

const VALID_TABS = ["story", "team", "press", "districts", "aipa"];

export default function AboutPage() {
  const [team, setTeam] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [press, setPress] = useState([]);
  const [sp, setSp] = useSearchParams();
  const initialTab = VALID_TABS.includes(sp.get("tab")) ? sp.get("tab") : "story";
  useEffect(() => {
    api.get("/team").then((r) => setTeam(r.data));
    api.get("/districts").then((r) => setDistricts(r.data));
    api.get("/press").then((r) => setPress(r.data));
  }, []);

  return (
    <div data-testid="about-page">
      <PageHero eyebrow="Our story" title="ABOUT JPA" subtitle="The Jharkhand Pickleball Association is the state's official governing body — nurturing talent from grass-roots to national stage."
        image="https://images.unsplash.com/photo-1718247528937-07667d8906d9?w=1800" />
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-14">
        <Tabs value={initialTab} onValueChange={(v) => { const next = new URLSearchParams(sp); if (v === "story") next.delete("tab"); else next.set("tab", v); setSp(next, { replace: true }); }}>
          <TabsList className="bg-jpa-surface rounded-sm h-auto p-1 flex-wrap gap-1">
            <TabsTrigger value="story" data-testid="tab-story" className="uppercase tracking-widest text-[11px] md:text-sm font-bold data-[state=active]:bg-jpa-primary data-[state=active]:text-white rounded-sm px-3 md:px-6 py-2">Our story</TabsTrigger>
            <TabsTrigger value="team" data-testid="tab-team" className="uppercase tracking-widest text-[11px] md:text-sm font-bold data-[state=active]:bg-jpa-primary data-[state=active]:text-white rounded-sm px-3 md:px-6 py-2">Team behind</TabsTrigger>
            <TabsTrigger value="press" data-testid="tab-press" className="uppercase tracking-widest text-[11px] md:text-sm font-bold data-[state=active]:bg-jpa-primary data-[state=active]:text-white rounded-sm px-3 md:px-6 py-2">Press centre</TabsTrigger>
            <TabsTrigger value="districts" data-testid="tab-districts" className="uppercase tracking-widest text-[11px] md:text-sm font-bold data-[state=active]:bg-jpa-primary data-[state=active]:text-white rounded-sm px-3 md:px-6 py-2">Districts</TabsTrigger>
            <TabsTrigger value="aipa" data-testid="tab-aipa" className="uppercase tracking-widest text-[11px] md:text-sm font-bold data-[state=active]:bg-jpa-primary data-[state=active]:text-white rounded-sm px-3 md:px-6 py-2">AIPA</TabsTrigger>
          </TabsList>

          <TabsContent value="story" className="mt-10 grid lg:grid-cols-2 gap-10">
            <div>
              <h2 className="font-display text-5xl tracking-wider">Our history</h2>
              <p className="mt-4 text-jpa-muted leading-relaxed">
                Founded in 2019 in Ranchi, the Jharkhand Pickleball Association (JPA) began as a small collective of 5 players
                who saw pickleball as India&apos;s next big sport. Six years later, JPA has grown to over 300+ registered players
                across 13 affiliated districts, sanctioning more than 13 tournaments and producing India&apos;s first medal-winning
                pickleball athletes on the national circuit.
              </p>
              <p className="mt-4 text-jpa-muted leading-relaxed">
                We are proudly affiliated with the All India Pickleball Association (AIPA), enabling our tournaments to count
                towards national rankings and unlocking career pathways for Jharkhand&apos;s brightest players.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img src="https://res.cloudinary.com/mytasjvq/image/upload/v1783856511/ANI-20260415094304_cze4kd.jpg" alt="JPA moment" className="aspect-square object-cover" />
              <img src="https://res.cloudinary.com/mytasjvq/image/upload/v1783858783/unnamed_3_rkfunj.jpg" alt="JPA moment" className="aspect-square object-cover mt-8" />
              <img src="https://res.cloudinary.com/mytasjvq/image/upload/v1786720597/556965877_1457820999687825_5172475590492645650_n_ruk5i9.jpg" alt="JPA moment" className="aspect-square object-cover" />
              <img src="https://res.cloudinary.com/mytasjvq/image/upload/v1786720607/496864598_1328669889269604_4550506317437259705_n_bvsmmp.jpg" alt="JPA moment" className="aspect-square object-cover mt-8" />
            </div>
          </TabsContent>

          <TabsContent value="team" className="mt-10 grid sm:grid-cols-2 md:grid-cols-3 gap-6">
            {team.map((m) => (
              <div key={m.id} data-testid={`team-card-${m.id}`} className="border border-jpa-border bg-white">
                <div className="aspect-[4/5] overflow-hidden bg-jpa-surface">
                  {m.image_url && <img src={m.image_url} alt={m.name} className="w-full h-full object-cover" />}
                </div>
                <div className="p-5">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-jpa-primary">{m.role}</div>
                  <h3 className="font-display text-2xl tracking-wider mt-1">{m.name}</h3>
                  <p className="mt-2 text-sm text-jpa-muted">{m.bio}</p>
                </div>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="press" className="mt-10 space-y-4">
            {press.map((p) => (
              <div key={p.id} data-testid={`press-item-${p.id}`} className="border-l-4 border-jpa-primary bg-jpa-surface p-5 md:p-6 rounded-r-sm">
                <div className="text-xs font-bold uppercase tracking-widest text-jpa-primary">{p.date}</div>
                <h3 className="font-display text-xl md:text-2xl tracking-wider mt-1 break-words leading-tight">{p.title}</h3>
                <p className="mt-2 text-jpa-muted text-sm md:text-base break-words">{p.body}</p>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="districts" className="mt-10">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {districts.map((d) => (
                <div key={d.id} data-testid={`district-${d.id}`} className="border border-jpa-border p-5 bg-white flex items-center gap-4">
                  <div className="w-12 h-12 bg-jpa-primary text-white grid place-items-center rounded-sm"><Award className="w-6 h-6" /></div>
                  <div>
                    <div className="font-display text-2xl tracking-wider">{d.name}</div>
                    <div className="text-xs text-jpa-muted">Affiliated since {d.affiliated_since} • President: {d.president}</div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="aipa" className="mt-10 grid md:grid-cols-3 gap-8 items-center">
            <div className="md:col-span-2">
              <span className="bg-jpa-accent text-jpa-ink px-3 py-1 text-xs font-bold uppercase tracking-widest skew-badge">Official affiliate</span>
              <h2 className="font-display text-5xl tracking-wider mt-3">Affiliated with AIPA</h2>
              <p className="mt-4 text-jpa-muted leading-relaxed">
                JPA is an officially recognised state chapter of the <b>All India Pickleball Association (AIPA)</b>. Our sanctioned
                tournaments feed into the national rankings, and our top players qualify directly for AIPA national events.
              </p>
              <p className="mt-4 text-jpa-muted leading-relaxed">
                Affiliation gives our members access to national-level referees, technical officials, coach certifications and
                pathways to represent India at international tournaments.
              </p>
              <div className="mt-6 text-xs uppercase tracking-widest text-jpa-muted">Affiliation number: <span className="text-jpa-ink font-bold">JPA/AIPA/2024/JH</span></div>
            </div>
            <div className="border-2 border-jpa-primary p-6 md:p-8 bg-white text-center rounded-sm shadow-lg">
              <img
                src="/logos/aipa-logo.png"
                alt="AIPA — All India Pickleball Association"
                data-testid="about-aipa-logo"
                className="w-40 md:w-48 h-auto object-contain mx-auto"
              />
              <div className="mt-4 font-display text-2xl tracking-wider text-jpa-primary">AIPA</div>
              <div className="mt-1 text-[10px] uppercase tracking-widest text-jpa-muted">All India Pickleball Association</div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
